// Utility helpers for phone/email formatting and date sorting.

// User's date-format preference — set from AuthProvider once /auth/me resolves.
// Default matches the historical DD/MM/YYYY behaviour so India ops don't shift.
let _datePref = "DD/MM/YYYY";
export const setDatePref = (fmt) => {
  if (fmt === "DD/MM/YYYY" || fmt === "MM/DD/YYYY") _datePref = fmt;
};
export const getDatePref = () => _datePref;

// Strip trailing ".0" (Excel float artefact), non-digits except leading +
export const normalizePhone = (raw) => {
  if (!raw) return "";
  let s = String(raw).trim();
  if (s.endsWith(".0")) s = s.slice(0, -2);
  s = s.replace(/[^\d+]/g, "");
  if (!s) return "";
  if (!s.startsWith("+")) s = "+" + s.replace(/^\+*/, "");
  return s;
};

export const waLink = (phone) => {
  const p = normalizePhone(phone).replace("+", "");
  return p ? `https://wa.me/${p}` : "";
};

export const PhoneLink = ({ value }) => {
  const p = normalizePhone(value);
  if (!p) return "—";
  const href = waLink(p);
  return (
    <a href={href} target="_blank" rel="noreferrer"
       className="text-brand hover:underline whitespace-nowrap" title="Open WhatsApp">
      {p}
    </a>
  );
};

export const EmailLink = ({ value }) => {
  if (!value) return "—";
  return (
    <a href={`mailto:${value}`} className="text-brand hover:underline whitespace-nowrap" title="Send email">
      {value}
    </a>
  );
};

// Format duration text: strip trailing ".0" from Excel-imported numbers.
export const formatDuration = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const s = String(v).trim();
  if (/^\d+\.0+$/.test(s)) return s.split(".")[0];
  return s;
};

// Format an ISO date (or a partial "YYYY-MM-DD") using the current user's
// date-format preference (DD/MM/YYYY by default, MM/DD/YYYY for US users).
export const fmtDate = (iso) => {
  if (!iso) return "";
  const s = String(iso);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  let y, mo, d;
  if (m) { y = m[1]; mo = m[2]; d = m[3]; }
  else {
    const dt = new Date(s);
    if (isNaN(dt)) return s;
    y = dt.getFullYear();
    mo = String(dt.getMonth() + 1).padStart(2, "0");
    d = String(dt.getDate()).padStart(2, "0");
  }
  return _datePref === "MM/DD/YYYY" ? `${mo}/${d}/${y}` : `${d}/${mo}/${y}`;
};

// Format an ISO timestamp as DD/MM/YYYY HH:MM (24h) — or MM/DD/YYYY h:MM AM/PM
// (12h) for users on the US date-format preference.
export const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  if (_datePref === "MM/DD/YYYY") {
    // 12-hour clock for US users. e.g. "02/24/2026 3:07 PM"
    const h24 = d.getHours();
    const h12 = ((h24 + 11) % 12) + 1;
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ampm = h24 >= 12 ? "PM" : "AM";
    return `${mm}/${dd}/${d.getFullYear()} ${h12}:${mi} ${ampm}`;
  }
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`;
};

// Sort dated rows so today is first, then future ascending, past at the very bottom.
export const sortByUpcoming = (rows, key) => {
  const today = new Date().toISOString().slice(0, 10);
  return [...rows].sort((a, b) => {
    const av = (a?.[key] || "").slice(0, 10);
    const bv = (b?.[key] || "").slice(0, 10);
    const aFuture = av >= today, bFuture = bv >= today;
    if (aFuture && !bFuture) return -1;
    if (!aFuture && bFuture) return 1;
    if (aFuture && bFuture) return av.localeCompare(bv);
    // both past → newest first
    return bv.localeCompare(av);
  });
};

// Program-booking sort: **Current first, Future later, Past at the bottom.**
// "Current" = the participant is already on the ground (start_date ≤ today ≤
// end_date). "Future" = start_date is still in the future. Within each bucket
// we sort ascending by the relevant date (currents by end soonest, futures by
// start soonest) so the operations team's eye lands on the arrivals they need
// to act on next. Past rows sort newest-first only when they slip through
// (the backend already tabs them out).
// Bucket labelling helper — used by the Program Booking table to insert a
// tiny "Current" / "Future" divider row between the two groups.
export const bookingBucket = (row) => {
  const today = new Date().toISOString().slice(0, 10);
  const start = (row?.start_date || "").slice(0, 10);
  const end = (row?.end_date || "").slice(0, 10);
  if (start && start > today) return "future";
  if (end && end < today) return "past";
  if (start && start <= today) return "current";  // in-progress
  return "future";  // undated → treat as future so it isn't hidden
};

// Small human-readable chip describing how urgent a booking is for GEHM.
// * "leaves tomorrow" / "leaves today" for participants about to depart
// * "on the ground" while the participant is in-country
// * "arrives tomorrow" / "arrives in 3 days" for imminent arrivals
// * "arrives Mar 12" for anything > 14 days out
// Returns null when there isn't enough date data to decide.
export const arrivalChip = (row) => {
  if (!row) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const parse = (s) => { if (!s) return null; const d = new Date(s.slice(0, 10) + "T00:00:00"); return isNaN(d) ? null : d; };
  const start = parse(row.start_date);
  const end = parse(row.end_date);
  const dayMs = 86400000;
  const diff = (d) => Math.round((d - today) / dayMs);
  // Departure urgency wins whenever they're already in-country and leaving soon.
  if (start && end && start <= today && today <= end) {
    const dEnd = diff(end);
    if (dEnd <= 0) return { label: "leaves today", cls: "bg-red-100 text-red-700 border-red-300" };
    if (dEnd === 1) return { label: "leaves tomorrow", cls: "bg-red-100 text-red-700 border-red-300" };
    if (dEnd <= 7) return { label: `leaves in ${dEnd} days`, cls: "bg-amber-100 text-amber-800 border-amber-300" };
    return { label: "on the ground", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" };
  }
  if (start) {
    const dStart = diff(start);
    if (dStart < 0) return { label: "trip ended", cls: "bg-slate-100 text-slate-500 border-slate-200" };
    if (dStart === 0) return { label: "arrives today", cls: "bg-red-100 text-red-700 border-red-300" };
    if (dStart === 1) return { label: "arrives tomorrow", cls: "bg-red-100 text-red-700 border-red-300" };
    if (dStart <= 7) return { label: `arrives in ${dStart} days`, cls: "bg-amber-100 text-amber-800 border-amber-300" };
    if (dStart <= 14) return { label: `arrives in ${dStart} days`, cls: "bg-sky-100 text-sky-700 border-sky-300" };
    return { label: `arrives ${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  }
  return null;
};

// Founder-dashboard grouping bucket for the "heart-rate view":
// Currently on Ground | Landing This Week | Landing This Month | Landing Later
export const durationBucketKey = (row) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const start = row?.start_date ? new Date(row.start_date.slice(0, 10) + "T00:00:00") : null;
  const end = row?.end_date ? new Date(row.end_date.slice(0, 10) + "T00:00:00") : null;
  if (start && end && start <= today && today <= end) return "Currently on Ground";
  if (!start) return "No start date";
  const days = Math.round((start - today) / 86400000);
  if (days < 0) return "Past";
  if (days <= 7) return "Landing This Week";
  if (days <= 31) return "Landing This Month";
  return "Landing Later";
};

export const sortCurrentThenFuture = (rows) => {
  const rank = { current: 0, future: 1, past: 2 };
  return [...rows].sort((a, b) => {
    const ba = bookingBucket(a), bb = bookingBucket(b);
    if (ba !== bb) return rank[ba] - rank[bb];
    if (ba === "current") {
      // Ends soonest first — the participant leaving tomorrow needs
      // attention before the one leaving in a month.
      return ((a.end_date || "") + "").localeCompare((b.end_date || "") + "");
    }
    if (ba === "future") {
      return ((a.start_date || "") + "").localeCompare((b.start_date || "") + "");
    }
    // past → newest end_date first
    return ((b.end_date || "") + "").localeCompare((a.end_date || "") + "");
  });
};
